import { ChatSidebar } from '@/components/chat/ChatSidebar';
import { MessageInput } from '@/components/chat/MessageInput';
import { MessageList } from '@/components/chat/MessageList';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { chatService, type WidgetUser } from '@/services/chat/chatService';
import {
  createChatService,
  type ChatService,
} from '@/services/chat/chatSocket.oi';
import type { Message } from '@/components/chat/types';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, User as UserIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

export function Chat() {
  const [selectedUser, setSelectedUser] = useState<WidgetUser | null>(null);
  const [message, setMessage] = useState('');
  const chatServiceRef = useRef<ChatService | null>(null);
  const socketUrl = import.meta.env.VITE_CHAT_SOCKET_URL;
  const queryClient = useQueryClient();

  const {
    data: chatUsersData,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['chatUsers'],
    queryFn: ({ pageParam = 1 }) =>
      chatService.getChatUsers({ page: pageParam, limit: 30 }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  });

  const {
    data: chatMessagesData,
    fetchNextPage: fetchNextMessagesPage,
    hasNextPage: hasNextMessagesPage,
    refetch: refetchMessages,
    isLoading: isLoadingMessages,
    isFetchingNextPage: isFetchingNextMessagesPage,
  } = useInfiniteQuery<{
    data: Message[];
    meta: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }>({
    queryKey: ['chatUserMessages', selectedUser?.id],
    queryFn: async ({ pageParam = 1 }) => {
      if (!selectedUser?.id) {
        return {
          data: [],
          meta: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 30,
          },
        };
      }
      return chatService.getChatUserMessagesById(selectedUser.id, {
        page: pageParam as number,
        limit: 30,
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.meta?.currentPage < lastPage?.meta?.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!selectedUser?.id,
  });

  // 🔹 Global status & widget user handlers
  const handleStatusChange = useCallback(
    (data: { userId: string; isOnline: boolean }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(['chatUsers'], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((u: WidgetUser) =>
              u.id === data.userId ? { ...u, isOnline: data.isOnline } : u
            ),
          })),
        };
      });

      setSelectedUser((prev) =>
        prev?.id === data.userId ? { ...prev, isOnline: data.isOnline } : prev
      );
    },
    [queryClient]
  );

  const handleNewWidgetUser = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['chatUsers'] });
  }, [queryClient]);

  const handleNewMessage = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (msg: any) => {
      console.log('📩 New message received:', msg);
      refetchMessages();
    },
    [refetchMessages]
  );

  // ✅ Connect socket once globally
  useEffect(() => {
    chatServiceRef.current = createChatService();
    chatServiceRef.current.connectSocket(socketUrl, {
      onMessage: handleNewMessage,
      onStatusChange: handleStatusChange,
      onNewWidgetUser: handleNewWidgetUser,
    });

    return () => chatServiceRef.current?.disconnectSocket();
  }, [socketUrl, handleNewMessage, handleStatusChange, handleNewWidgetUser]);

  // 🔹 Send message
  const sendMessage = async () => {
    if (!selectedUser || !message.trim()) return;
    try {
      await chatServiceRef.current?.sendMessage(
        message.trim(),
        selectedUser.id
      );
      setMessage('');
      refetchMessages();
    } catch (err) {
      console.error('Send error:', err);
    }
  };

  const allUsers = chatUsersData?.pages.flatMap((p) => p.data) || [];
  const allMessages: Message[] =
    chatMessagesData?.pages
      .flatMap((p) => (p?.data as Message[]) ?? [])
      .reverse() || [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-100px)] bg-white">
      <ChatSidebar
        users={allUsers}
        selectedUser={selectedUser}
        onSelectUser={setSelectedUser}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoading={isFetching}
      />

      <Card className="flex-1 col-span-3 lg:col-span-2 flex flex-col h-full">
        {selectedUser ? (
          <>
            <CardHeader className="border-b p-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    className="inline-flex lg:!hidden"
                    onClick={() => setSelectedUser(null)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <Avatar className="h-9 w-9">
                    <AvatarFallback>
                      {selectedUser.firstName?.[0]?.toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {selectedUser.firstName
                        ? `${selectedUser.firstName} ${selectedUser.lastName || ''}`.trim()
                        : selectedUser.email || 'Anonymous User'}
                    </p>
                    <p
                      className={`text-xs ${
                        selectedUser.isOnline
                          ? 'text-green-500'
                          : 'text-gray-400'
                      }`}
                    >
                      {selectedUser.isOnline ? 'online' : 'offline'}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline">
                  <UserIcon className="h-4 w-4 mr-1" /> Profile
                </Button>
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-1 flex flex-col p-0">
              <MessageList
                onDiscountResponse={() => {}}
                editingDiscount={{ messageId: null, newPrice: '' }}
                setEditingDiscount={() => {}}
                messages={allMessages}
                onLoadMore={fetchNextMessagesPage}
                hasMore={hasNextMessagesPage}
                isLoadingInitial={isLoadingMessages}
                isLoadingMore={isFetchingNextMessagesPage}
              />
              <div className="border-t p-3">
                <MessageInput
                  message={message}
                  onMessageChange={setMessage}
                  onSendMessage={sendMessage}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </CardContent>
          </>
        ) : (
          <div className="flex flex-col justify-center items-center flex-1 text-muted-foreground">
            <p className="text-lg font-medium">
              Select a chat to start messaging
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
